// Core
import React, {
  ReactNode,
  useEffect,
} from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

// Libraries
import 'dayjs/locale/pt-br';
import { ConfigProvider } from 'antd';
import { Locale } from 'antd/es/locale';
import { ThemeProvider } from 'styled-components';
import dayjs from 'dayjs';
import enUS from 'antd/lib/locale/en_US';
import ptBR from 'antd/lib/locale/pt_BR';

// Types
import {
  User,
  LengthType,
  MeasurementType,
  PressureType,
  TemperatureType,
} from './types/global';

// Locale
import i18n, {
  fallbackLanguage,
  initI18n,
  LangMap,
  langMap,
} from './locale/i18n';

// Store
import { getLanguage, setLanguage } from './store/app.store';
import {
  getMeasurementUnit,
  setMeasurementUnit,
} from './store/measurement.store';
import { getUser } from './store/session.store';

// Hooks
import { BreakpointProvider } from './hooks/useBreakpoint';

// Plugins
import { appLocaleToDayjs, validateEnvironmentVariables } from './plugins/general';

// Theme
import theme, { antDTheme } from './config/theme';

// View
import { AppError } from './views/AppError';

// Layout
import { Toast } from './layout/elements/Toast';

// Routes
import routes from './Routes';

initI18n();

function App() {
  // Dependencies
  const router = createBrowserRouter(routes);

  const [envVarsErrors, setEnvVarsErrors] = React.useState<string>();

  // Getters
  const language: string | null = getLanguage();
  const lengthUnit: LengthType = getMeasurementUnit(MeasurementType.length);
  const pressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);
  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);
  const user: User | null = getUser();

  /* **********************************************************************************************
  ************************************* COMPONENT HANDLING ****************************************
  ********************************************************************************************** */

  useEffect((): void => { // Set the language
    let targetLanguage: string | null = null;

    // Priority 1: Use stored language if it exists (user manually changed)
    if (language) {
      targetLanguage = language;
    } else if (user && 'language' in user && user.language) {
      // Priority 2: Use language from JWT if user is authenticated
      targetLanguage = user.language;
    } else {
      // Priority 3: Use browser language as fallback
      const browserLanguage: string = navigator.language.toLowerCase().replace('-', '_');
      const isValidLanguage = (key: string, obj: LangMap): key is keyof typeof obj => key in obj;
      targetLanguage = isValidLanguage(browserLanguage, langMap)
        ? langMap[browserLanguage]
        : fallbackLanguage;
    }

    if (targetLanguage) {
      i18n.changeLanguage(targetLanguage);
      dayjs.locale(appLocaleToDayjs(targetLanguage));

      // Only update stored language if it's different from current and not from user
      if (targetLanguage !== language && !(user && 'language' in user && user.language)) {
        setLanguage(targetLanguage);
      }
    }
  }, [language, user]);

  // Fallback to default unit if the unit is not valid or not set
  useEffect((): void => {
    if (!Object.values(PressureType).includes(pressureUnit)) {
      setMeasurementUnit(PressureType.MPa, MeasurementType.pressure);
    }

    if (!Object.values(TemperatureType).includes(temperatureUnit)) {
      setMeasurementUnit(TemperatureType.c, MeasurementType.temperature);
    }

    if (!Object.values(LengthType).includes(lengthUnit)) {
      setMeasurementUnit(LengthType.mm, MeasurementType.length);
    }
  }, [pressureUnit, temperatureUnit, lengthUnit]);

  useEffect((): void => {
    try {
      validateEnvironmentVariables();
    } catch (error: unknown) {
      if (error instanceof Error) {
        setEnvVarsErrors(error.message);
      }
    }
  }, []);

  let appContent: ReactNode;

  if (envVarsErrors) {
    appContent = <AppError errorMessage={envVarsErrors} />;
  } else {
    appContent = (
      <>
        <RouterProvider router={router} />
        <Toast />
      </>
    );
  }

  const mapAntDLocale: Record<string, Locale> = {
    en_US: enUS,
    pt_BR: ptBR,
  };

  return (
    <ConfigProvider theme={antDTheme.default} locale={mapAntDLocale[language || fallbackLanguage]}>
      <ThemeProvider theme={theme.default}>
        <BreakpointProvider>
          <div className="App">
            {appContent}
          </div>
        </BreakpointProvider>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;
